import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { graphierQLNextClient, sortMapNestedObject } from "utils";

import {
	GetMapQuery,
	GetMapQueryVariables,
	Map,
	Tool,
} from "~map-view/types/__generated/gql/graphql";

const query = /* GraphQL */ `
	fragment SymbolStyleFragment on SymbolStyle {
		name
		isGraphic
		basic {
			size
			rotation
			symbol
			radius
			opacity
		}
		fill {
			width
			fillType
			style
			color
			opacity
			isDisabled
		}
		stroke {
			style
			color
			width
			opacity
			isDisabled
		}
	}

	fragment SymbolValueFragment on SymbolValue {
		color
		value
		label
		count
		symbol {
			...SymbolStyleFragment
		}
		id
		range
		legendVisibility
		min
		max
	}

	query GetMap($id: ID!) {
		getMapById(id: $id) {
			id
			createdAt
			updatedAt
			name
			description
			userId
			baseMapId
			tools {
				type
			}
			layerGroupCollection {
				total
				items {
					id
					createdAt
					updatedAt
					mapId
					visible
					position
					layerCollection {
						total
						items {
							id
							createdAt
							updatedAt
							name
							description
							minZoom
							maxZoom
							position
							visible
							datasetId
							layerGroupId
							dataType
							styleType
							labelsStyle {
								attribute
								font
								fontSize
								fontColour
								fontOpacity
								haloColour
								haloOpacity
								haloSize
							}
							lineSimpleStyle {
								symbolStyle {
									...SymbolStyleFragment
								}
							}
							lineCategoryStyle {
								colorRamp
								symbolStyle {
									...SymbolStyleFragment
								}
								valueField
								symbolValues {
									...SymbolValueFragment
								}
							}
							polygonCategoryStyle {
								colorRamp
								symbolStyle {
									...SymbolStyleFragment
								}
								valueField
								symbolValues {
									...SymbolValueFragment
								}
							}
							lineQuantityStyle {
								valueField
								legendType
								classNumber
								colorRamp
								classification
								symbolStyle {
									...SymbolStyleFragment
								}
								symbolValues {
									...SymbolValueFragment
								}
								classbreaks
							}
							pointSimpleStyle {
								symbolStyle {
									...SymbolStyleFragment
								}
							}
							pointCategoryStyle {
								colorRamp
								symbolStyle {
									...SymbolStyleFragment
								}
								valueField
								symbolValues {
									...SymbolValueFragment
								}
							}
							pointQuantityStyle {
								valueField
								legendType
								classNumber
								colorRamp
								classification
								symbolStyle {
									...SymbolStyleFragment
								}
								symbolValues {
									...SymbolValueFragment
								}
								classbreaks
							}
							heatMapStyle {
								basic {
									radius
									opacity
								}
							}
							polygonSimpleStyle {
								symbolStyle {
									...SymbolStyleFragment
								}
							}
							bubbleStyle {
								valueField
								classNumber
								classification
								radius {
									min
									max
								}
								symbolStyle {
									...SymbolStyleFragment
								}
								symbolValues {
									...SymbolValueFragment
								}
							}
							polygonQuantityStyle {
								valueField
								legendType
								classNumber
								colorRamp
								classification
								symbolStyle {
									...SymbolStyleFragment
								}
								symbolValues {
									...SymbolValueFragment
								}
								classbreaks
							}

							_dataset {
								id
								createdAt
								updatedAt
								name
								description
								userId
								type
								datasourceType
								sourceDatasetId
								size
								mapsCount
								legendUrl
								getCapabilitiesUrl
								isPrivate
								bbox
							}
						}
					}
				}
			}
		}
	}
`;

export const getMapByIdQueryKey = (id: string) => ["getMapById", id];

export const useGetMapById = (id: string) => {
	return useQuery({
		enabled: !!id,
		queryKey: getMapByIdQueryKey(id),
		queryFn: () =>
			graphierQLNextClient<GetMapQuery, GetMapQueryVariables>({
				query,
				variables: { id },
				useKey: true,
			}).then((res) => sortMapNestedObject(res.getMapById)),
	});
};

export const useGetCurrentMap = () => {
	const {
		query: { "map-id": mapId },
	} = useRouter();

	return useGetMapById(mapId as string);
};

export const isToolEnabled = ({
	map,
	tool,
}: {
	map: Pick<Map, "tools">;
	tool: Tool;
}) => {
	return map.tools?.some((t) => t && t.type === tool.type) ?? false;
};
